import {
  DISPLAY_FONTS,
  DISPLAY_FONT_LABELS,
  DISPLAY_FONT_SIZES,
  type DisplayFont,
  type DisplayFontSize,
  type Typography,
} from '@gb-ticket/shared-types';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ApiRequestError } from '@/lib/api';
import { usePublicBrand } from '@/platform/brand/use-public-brand';
import { useOwnProfile, useUpdateOwnProfile } from '@/platform/features/users/use-own-profile';
import {
  applyTypography,
  browserTypographyStorage,
  fontFamilyFor,
  formatFontSizeLabel,
  writeTypography,
} from '@/platform/theme/typography';

const SAVE_FAILED = 'Die Schrift konnte nicht gespeichert werden.';

function indexOf<T extends string>(values: readonly T[], value: T): number {
  const index = values.indexOf(value);
  return index < 0 ? 0 : index;
}

function TypographyPreview({ value }: { value: Typography }) {
  return (
    <div
      aria-hidden="true"
      className="rounded-md border border-border p-3"
      style={{
        fontFamily: fontFamilyFor(value.displayFont),
        fontSize: `${value.displayFontSize}px`,
      }}
    >
      <p className="font-semibold" style={{ fontSize: '0.875em', margin: 0 }}>
        Intranet
      </p>
      <p style={{ fontSize: '1em', margin: '0.5em 0' }}>Größe prüfen</p>
      <span
        style={{
          display: 'inline-block',
          fontSize: '0.875em',
          padding: '0.25em 0.6em',
          borderRadius: '0.25em',
          background: 'var(--primary)',
          color: 'var(--primary-foreground)',
        }}
      >
        Aktion
      </span>
    </div>
  );
}

export function TypographyCard({
  mode,
  saved,
  brand,
  onSave,
}: {
  mode: 'profile' | 'tenant';
  /** Null means the person follows the tenant. Tenant mode always passes a pair. */
  saved: Typography | null;
  brand: Typography;
  onSave: (next: Typography | null) => Promise<void>;
}) {
  const initial = saved ?? brand;
  const [fontIndex, setFontIndex] = useState(() => indexOf(DISPLAY_FONTS, initial.displayFont));
  const [sizeIndex, setSizeIndex] = useState(() =>
    indexOf(DISPLAY_FONT_SIZES, initial.displayFontSize),
  );
  const [followTenant, setFollowTenant] = useState(mode === 'profile' && saved == null);
  const [saving, setSaving] = useState(false);

  const chosen: Typography = {
    displayFont: DISPLAY_FONTS[fontIndex] ?? 'system',
    displayFontSize: DISPLAY_FONT_SIZES[sizeIndex] ?? '16',
  };
  const dirty =
    mode === 'tenant'
      ? saved?.displayFont !== chosen.displayFont ||
        saved.displayFontSize !== chosen.displayFontSize
      : followTenant
        ? saved != null
        : saved?.displayFont !== chosen.displayFont ||
          saved?.displayFontSize !== chosen.displayFontSize;

  async function save() {
    setSaving(true);
    try {
      await onSave(mode === 'profile' && followTenant ? null : chosen);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Schrift</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_16rem]">
          <div className="space-y-6">
            <FontSlider
              id="typography-font"
              label="Schrift"
              valueLabel={DISPLAY_FONT_LABELS[chosen.displayFont]}
              value={fontIndex}
              max={DISPLAY_FONTS.length - 1}
              onChange={(next) => {
                setFollowTenant(false);
                setFontIndex(next);
              }}
            />
            <FontSlider
              id="typography-size"
              label="Größe"
              valueLabel={formatFontSizeLabel(chosen.displayFontSize)}
              value={sizeIndex}
              max={DISPLAY_FONT_SIZES.length - 1}
              onChange={(next) => {
                setFollowTenant(false);
                setSizeIndex(next);
              }}
            />
          </div>
          <TypographyPreview value={chosen} />
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <Button type="button" disabled={!dirty || saving} onClick={() => void save()}>
            Speichern
          </Button>
          {mode === 'profile' ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setFontIndex(indexOf(DISPLAY_FONTS, brand.displayFont));
                setSizeIndex(indexOf(DISPLAY_FONT_SIZES, brand.displayFontSize));
                setFollowTenant(true);
              }}
            >
              Vorgabe übernehmen
            </Button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

function FontSlider({
  id,
  label,
  valueLabel,
  value,
  max,
  onChange,
}: {
  id: string;
  label: string;
  valueLabel: string;
  value: number;
  max: number;
  onChange: (next: number) => void;
}) {
  return (
    <div className="space-y-2">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      <input
        id={id}
        className="w-full accent-primary"
        type="range"
        min={0}
        max={max}
        step={1}
        value={value}
        aria-valuetext={valueLabel}
        onChange={(event) => onChange(Number(event.target.value))}
      />
      <p className="text-sm text-muted-foreground">{valueLabel}</p>
    </div>
  );
}

function pairFromBrand(
  brand:
    | {
        displayFont?: DisplayFont;
        displayFontSize?: DisplayFontSize;
      }
    | undefined,
): Typography {
  return {
    displayFont: brand?.displayFont ?? 'system',
    displayFontSize: brand?.displayFontSize ?? '16',
  };
}

export function ProfileTypographyCard() {
  const profile = useOwnProfile();
  const brandQuery = usePublicBrand();
  const update = useUpdateOwnProfile();
  const brand = pairFromBrand(brandQuery.data);
  const saved: Typography | null =
    profile.data?.displayFont && profile.data.displayFontSize
      ? { displayFont: profile.data.displayFont, displayFontSize: profile.data.displayFontSize }
      : null;

  if (brandQuery.isPending || (profile.isPending && !profile.data)) {
    return <p>Schrift wird geladen …</p>;
  }
  if (brandQuery.isError) {
    return <p>Die Schrift konnte nicht geladen werden.</p>;
  }

  return (
    <TypographyCard
      key={`${saved?.displayFont ?? 'tenant'}:${saved?.displayFontSize ?? 'tenant'}:${brand.displayFont}:${brand.displayFontSize}`}
      mode="profile"
      saved={saved}
      brand={brand}
      onSave={async (next) => {
        try {
          await update.mutateAsync(
            next
              ? { displayFont: next.displayFont, displayFontSize: next.displayFontSize }
              : { displayFont: null, displayFontSize: null },
          );
          const applied = next ?? brand;
          writeTypography(browserTypographyStorage(), applied);
          applyTypography(document.documentElement, applied);
          toast.success('Schrift gespeichert.');
        } catch (error) {
          toast.error(error instanceof ApiRequestError ? error.message : SAVE_FAILED);
        }
      }}
    />
  );
}
